/**
 * IdentityAvatar - Standalone avatar component
 * 
 * SHARED COMPONENT - Minimal avatar display for lists, stacks, etc.
 * 
 * Usage:
 *   const { IdentityAvatar } = (globalThis as any).MembershipComponents || {};
 *   <IdentityAvatar type="person" name="John" status="active" size={32} />
 */

import * as React from 'react';

export type IdentityType = 'person' | 'group' | 'account' | 'agent' | 'service';
export type IdentityStatus = 'active' | 'inactive' | 'orphaned' | 'pending';

export interface IdentityAvatarProps {
  /** Identity type (determines icon and shape) */
  type: IdentityType;
  /** Display name (for alt text and initials) */
  name: string;
  /** Avatar image URL */
  avatar?: string;
  /** Status for color */
  status?: IdentityStatus;
  /** Size in pixels (default: 32) */
  size?: number;
  /** Show status ring (default: false) */
  showStatusRing?: boolean;
  /** Click handler */
  onClick?: () => void;
}

const TYPE_ICONS: Record<IdentityType, string> = {
  person: '👤',
  group: '👥',
  account: '🔐',
  agent: '🤖',
  service: '⚙️',
};

const STATUS_COLORS: Record<IdentityStatus, string> = {
  active: 'var(--success-color, #10b981)',
  inactive: 'var(--main-text-secondary, #6b7280)',
  orphaned: 'var(--warning-color, #f59e0b)',
  pending: 'var(--info-color, #3b82f6)',
};

export function IdentityAvatar({
  type,
  name,
  avatar,
  status = 'active',
  size = 32,
  showStatusRing = false,
  onClick,
}: IdentityAvatarProps) {
  const statusColor = STATUS_COLORS[status];
  const isRound = type === 'person' || type === 'agent';
  const iconSize = Math.max(12, Math.floor(size * 0.45));

  const avatarStyle: React.CSSProperties = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: isRound ? '50%' : `${Math.floor(size * 0.15)}px`,
    background: `linear-gradient(135deg, ${statusColor}, color-mix(in srgb, ${statusColor} 70%, var(--main-accent, #8b5cf6)))`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: `${iconSize}px`,
    cursor: onClick ? 'pointer' : 'default',
    boxShadow: showStatusRing 
      ? `0 0 0 2px var(--card-bg, #1e1e2e), 0 0 0 4px ${statusColor}`
      : `0 2px 4px ${statusColor}40`,
    transition: 'all 0.2s ease',
    flexShrink: 0,
  };

  return React.createElement('div', {
    style: avatarStyle,
    onClick,
    role: onClick ? 'button' : 'img',
    tabIndex: onClick ? 0 : undefined,
    'aria-label': `${name} (${type}, ${status})`,
    title: name,
  },
    avatar
      ? React.createElement('img', {
          src: avatar,
          alt: name,
          style: {
            width: '100%',
            height: '100%',
            borderRadius: 'inherit',
            objectFit: 'cover',
          }
        })
      : TYPE_ICONS[type]
  );
}

export default IdentityAvatar;

